import { window } from "global";
import { useEffect } from "react";
import { usePageTitleAndDesc } from "@ugg/shared/utils/seo-helpers";

const FAQ = () => {
  const getPageTitleAndDesc = usePageTitleAndDesc();

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  return (
    <div className="text-content-container content-side-padding">
      {getPageTitleAndDesc("faq")}
      <div className="text-content">
        <h1>Frequently Asked Questions</h1>
        <h2>Why U.GG?</h2>
        <p>
          U.GG is the most accurate League of Legends Statistics site. If there are 30 seconds left in champ select and you have
          no idea what runes to take, U.GG is your quick and easy solution to win the lane. If you don't even know what champion
          to play, U.GG provides the most up to date champion tier lists and win rates, as well as counters to whatever your lane
          opponent picked. If you need do a deep dive on champion data to find out the most optimal builds and counters, U.GG is
          your most reliable source of data.
        </p>
        <h2>What is unique about U.GG?</h2>
        <p>
          We hold ourselves to the standard of having the best UI/UX and the most granular data. From the UI/UX perspective, we
          make it easy for you to navigate to whatever you want to see on U.GG. Every site claims to analyze every match. U.GG
          takes it one step further by also allowing you to see the most granular data. Filter by whatever you care about, and
          we'll show you exactly how many matches we've analyzed, and the insights we have derived from those matches.
        </p>
        <h2>How does U.GG monetize?</h2>
        <p>
          We serve ads. We also offer U.GG PLUS, our premium subscription service that unlocks an elevated experience for users.
          Subscribers get an ad-free interface across our website and app, personalized champion insights, counters, and tier
          lists, profile customizations, priority customer support, plus more perks added over time.{" "}
          <a href="https://u.gg/plus/">Learn more about U.GG PLUS.</a>
        </p>
        <h2>How do I navigate on U.GG?</h2>
        <p>
          We prioritize ease of use and user experience. In other words making sure you get the data you're looking for with the
          least amount of clicks. When you first navigate to U.GG you're met with our search bar. This search bar is your portal
          to all your champion build needs. Type any champion name or common nickname, like “Twisted Fate” or “TF” for example,
          and as soon as you hit enter we take you to the U.GG recommended build. If that isn't your cup of tea, try “Twisted Fate
          Pro Build” or “Twisted Fate Counter” to help navigate you directly in one click to the other sections of our site. For
          every champion, we offer a recommended build, a list of recent pro builds, champion counters, matchups, duos, detailed
          items and runes, and a whole lot more.
        </p>
        <h2>How does the Search Bar work?</h2>
        <p>
          Our Search Bar is a Smart Search Bar. When you start typing in a champion name you will be given the option to got to
          that champion's Build and Runes page, Pro Builds page, or Counters page. Pressing enter takes you to the selected
          option. After typing out a champion name (or part of a champion name) you can add “pro build” to go to their Pro Builds
          page, or “counters” to go to their counters page. The search bar is smart enough that you can also abbreviate, such as
          only adding “pro” to go to the Pro Builds page.
        </p>
        <h2>What is the U.GG Recommended Build?</h2>
        <p>
          The U.GG Recommended Build is an algorithmic approach to finding an optimal build for your specific situation. We
          analyze every game that fits the selected filter criteria, and return the most frequent build that holds a higher win
          rate than the baseline win rate for your selected champion. By default, the filter criteria is the Most Common Lane
          Assignment, Plat+ Ranking, All Regions, Current Patch, and Against All Champions.
        </p>
        <h2>What filtering options does U.GG offer?</h2>
        <p>
          We offer the most granular data available at U.GG. We offer filters in game type, role, rank, region and patch. For our
          Recommended Builds, we also allow you to define a lane opponent. For example, playing Zed vs Ahri is different than Zed
          vs Talon. Change the “Vs Champion” filter to look at what is best in your specific matchup. On our Tier lists, Matchups,
          and Duos pages we also offer the “show all champions” filter. Which allows you to see every champion's performance in a
          role or context of your choosing. While some players only care about Plat+ Korean data, we understand that other users
          want to see data applicable to them, so we made sure that players could see data exclusive to their rank and region.
        </p>
        <h2>Why are your tier lists sorted by win rate, despite being called a tier list?</h2>
        <p>
          At the end of the day, we believe win rate is the most obvious metric to sort by because if a champion wins more, it has
          a higher win rate. Also, Doublelift told us win rates are the most important metric. We understand pick rate and ban
          rate also factor into whether a champion is OP, and we factor those into our tier calculation.
        </p>
        <h2>How are champion tier calculated?</h2>
        <p>
          A champion tier is determined with an algorithm which takes win rate, pick rate, and weighted ban rate into account to
          assign the champion to a tier that displays how effective that champion is at winning in this patch's meta. We factor in
          pick rate and ban rate to help the user understand whether a champion is only good because one tricks play it, or if it
          is simply OP in the current patch.
        </p>
        <h2>How do you determine champion roles?</h2>
        <p>
          We start with the Riot API which assigns each champion in a game to a role. However, the reported roles are not always
          correct. This fact always surprises people. However, if you think about your personal experience with level 1 fiesta
          invades, role swaps, junglers forgetting smite and a host of other factors, it is easy to see how the API can be wrong
          at times. We created an algorithm that mimics a human heuristic for determining roles and we believe the algorithm is
          correct 99.9% of the time. This algorithm is what allows us to confidently provide real statistical insight into Korean
          bronze Vayne jungle builds. We don't care how sparse the data is because we are showing you exactly what happened. You
          can fact check for yourself by perusing our pro builds section for any champion. In each match, we have a post match
          section and we list the players involved in role order. First player is top, next is jungle, then mid, then adc and
          lastly support. You will see that our classifications always pass the eye test, regardless of whether there were two
          smites, no smites, 3 teleports, 4 marksmen or anything other fiesta you can think of.
        </p>
        <h2>What are your champion counters and how are they calculated?</h2>
        <p>
          Our champion counters are what a particular champion is losing against the most on a particular patch. We calculate this
          by analyzing every game a particular champion is played, and then displaying which champions are winning the most and by
          how much.
        </p>
        <h2>Does U.GG support OpenSearch?</h2>
        <p>
          Yes! Typing “U.GG” + Tab into your browser lets you navigate directly to the U.GG page you're looking for from any
          browser's search bar.
        </p>
        <img
          className="mx-auto mt-[12px] max-xs:w-full"
          src="https://static.bigbrain.gg/assets/ugg/pages/faq/opensearch.gif"
          alt="How to use Open Search"
        />
        <h2>Why does my OpenSearch not work on chrome?</h2>
        <p>
          If your OpenSearch isn't working on chrome, you may be on a newer browser version where you need to active the shortcut.
          Navigate to your Chrome Settings &gt; Search engine &gt; Manage search engines and site search &gt; Search "u.gg" in the
          top right search bar &gt; Click "Activate" on the U.GG shortcut that appears under "Inactive shortcuts".
        </p>
        <h2>Where do you get your data?</h2>
        <p>
          All of our data comes from <a href="https://developer.riotgames.com/">Riot's API.</a>
        </p>
        <h2>How many games do you analyze?</h2>
        <p>
          We analyze every game available from Riot's API. This ensures that we have the largest possible sample size for our
          data.
        </p>
        <h2>Can you make a mobile app?</h2>
        <p>
          We initially operated under the assumption that anyone playing League of Legends is playing on a non-mobile device. Yes,
          even a potato counts as non-mobile. Our primary focus is on the PC web app experience, but we have not ruled out a
          mobile app in the (near) future.
        </p>
        <h2>Can you implement X feature?</h2>
        <p>
          If you have feature you would like to see on U.GG, please shoot us an email at <a href="mailto:info@u.gg">info@u.gg</a>{" "}
          with your suggestions.
        </p>
      </div>
    </div>
  );
};

export default FAQ;
